import { t } from 'i18next';
import { AccountOwnerType, DepositAccountType } from 'services/merchants/types';
import * as Yup from 'yup';

import getQueryParam from 'utils/url';

import { LABELS } from '../../keys';

export const FORM_FIELDS = {
  BUSINESS_NAME: 'businessName',
  BANK_NAME: 'bankName',
  ACCOUNT: 'account',
  ACCOUNT_OWNER_NAME: 'accountOwnerName',
  ACCOUNT_OWNER_TYPE: 'accountOwnerType',
  ADDRESS: 'address',
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
} as const;

export const NESTED_ACCOUNT_FIELDS = {
  ACCOUNT_NUMBER: 'accountNumber',
  ROUTING_NUMBER: 'routingNumber',
  CHECKING_OR_SAVINGS: 'checkingOrSavings',
  STREET_LINE_1: 'streetLine1',
  STREET_LINE_2: 'streetLine2',
  CITY: 'city',
  STATE: 'state',
  POSTAL_CODE: 'postalCode',
  COUNTRY: 'country',
} as const;

export const KEYS = {
  ROUTING_NUMBER_MAX_LENGTH: 9,
  UNASSOCIATED_ERROR: 'unassociatedError',
  // For this payment rail we support usd only
  // Even if merchant has currency USD we would need to lowercase it.
  // Hard coded currency brings less confusion in this case
  HARDCODED_CURRENCY: 'usd',
};

export type ACHFormField = (typeof FORM_FIELDS)[keyof typeof FORM_FIELDS];
export type ACHFormNestedField = (typeof NESTED_ACCOUNT_FIELDS)[keyof typeof NESTED_ACCOUNT_FIELDS];

export type ACHFormValues = {
  [FORM_FIELDS.ACCOUNT]: {
    [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: string;
    [NESTED_ACCOUNT_FIELDS.ROUTING_NUMBER]: string;
    [NESTED_ACCOUNT_FIELDS.CHECKING_OR_SAVINGS]: DepositAccountType;
  };
  [FORM_FIELDS.BUSINESS_NAME]: string | undefined;
  [FORM_FIELDS.BANK_NAME]: string;
  [FORM_FIELDS.ACCOUNT_OWNER_NAME]: string | undefined;
  [FORM_FIELDS.ACCOUNT_OWNER_TYPE]: AccountOwnerType;
  [FORM_FIELDS.FIRST_NAME]: string | undefined;
  [FORM_FIELDS.LAST_NAME]: string | undefined;
  [FORM_FIELDS.ADDRESS]: {
    [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: string;
    [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: string | undefined;
    [NESTED_ACCOUNT_FIELDS.CITY]: string;
    [NESTED_ACCOUNT_FIELDS.STATE]: string;
    [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: string;
    [NESTED_ACCOUNT_FIELDS.COUNTRY]: string;
  };
};

export type ACHNestedFormValues = {
  [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.ROUTING_NUMBER]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.CHECKING_OR_SAVINGS]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.CITY]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.STATE]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.COUNTRY]: string | undefined;
};

export const accountOwnerTypeOptions = [
  {
    key: AccountOwnerType.Individual,
    value: AccountOwnerType.Individual,
    label: t(LABELS.CREATE_MODAL.ACCOUNT_OWNER_TYPE.INDIVIDUAL),
  },
  {
    key: AccountOwnerType.Business,
    value: AccountOwnerType.Business,
    label: t(LABELS.CREATE_MODAL.ACCOUNT_OWNER_TYPE.BUSINESS),
  },
];

export const depositAccountOptions = [
  {
    key: DepositAccountType.CHECKING,
    label: t(LABELS.CREATE_MODAL.CHECKING_OR_SAVINGS.CHECKING),
  },
  {
    key: DepositAccountType.SAVINGS,
    label: t(LABELS.CREATE_MODAL.CHECKING_OR_SAVINGS.SAVINGS),
  },
];

export const getValidationSchema = () =>
  Yup.object().shape({
    [FORM_FIELDS.ACCOUNT]: Yup.object().shape({
      [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
      [NESTED_ACCOUNT_FIELDS.ROUTING_NUMBER]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
      [NESTED_ACCOUNT_FIELDS.CHECKING_OR_SAVINGS]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
    }),
    [FORM_FIELDS.BANK_NAME]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
    [FORM_FIELDS.ACCOUNT_OWNER_NAME]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
    [FORM_FIELDS.ACCOUNT_OWNER_TYPE]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
    [FORM_FIELDS.BUSINESS_NAME]: Yup.string().when([FORM_FIELDS.ACCOUNT_OWNER_TYPE], {
      is: (val: any) => String(val) === AccountOwnerType.Business,
      then: (schema) => schema.required(t(LABELS.ERRORS.REQUIRED_FIELD)),
      otherwise: (schema) => schema.optional(),
    }),
    [FORM_FIELDS.FIRST_NAME]: Yup.string().when([FORM_FIELDS.ACCOUNT_OWNER_TYPE], {
      is: (val: any) => String(val) === AccountOwnerType.Individual,
      then: (schema) => schema.required(t(LABELS.ERRORS.REQUIRED_FIELD)),
      otherwise: (schema) => schema.optional(),
    }),

    [FORM_FIELDS.LAST_NAME]: Yup.string().when([FORM_FIELDS.ACCOUNT_OWNER_TYPE], {
      is: (val: any) => String(val) === AccountOwnerType.Individual,
      then: (schema) => schema.required(t(LABELS.ERRORS.REQUIRED_FIELD)),
      otherwise: (schema) => schema.optional(),
    }),
    [FORM_FIELDS.ADDRESS]: Yup.object().shape({
      [NESTED_ACCOUNT_FIELDS.COUNTRY]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
      [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
      [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: Yup.string().optional(),
      [NESTED_ACCOUNT_FIELDS.CITY]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
      [NESTED_ACCOUNT_FIELDS.STATE]: Yup.string().when([NESTED_ACCOUNT_FIELDS.COUNTRY], {
        is: (val: any) => String(val) === 'USA',
        then: (schema) => schema.required(t(LABELS.ERRORS.REQUIRED_FIELD)),
        otherwise: (schema) => schema.optional(),
      }),
      [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
    }),
  });

const initialValues: ACHFormValues = {
  [FORM_FIELDS.BANK_NAME]: '',
  [FORM_FIELDS.ACCOUNT_OWNER_NAME]: undefined,
  [FORM_FIELDS.ACCOUNT]: {
    [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: '',
    [NESTED_ACCOUNT_FIELDS.ROUTING_NUMBER]: '',
    [NESTED_ACCOUNT_FIELDS.CHECKING_OR_SAVINGS]: undefined as any,
  },
  [FORM_FIELDS.ACCOUNT_OWNER_TYPE]: undefined as any,
  [FORM_FIELDS.BUSINESS_NAME]: undefined,
  [FORM_FIELDS.FIRST_NAME]: undefined,
  [FORM_FIELDS.LAST_NAME]: undefined,
  [FORM_FIELDS.ADDRESS]: {
    [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: '',
    [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: undefined,
    [NESTED_ACCOUNT_FIELDS.CITY]: '',
    [NESTED_ACCOUNT_FIELDS.STATE]: '',
    [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: '',
    [NESTED_ACCOUNT_FIELDS.COUNTRY]: '',
  },
};

export const getPrefilledValues = (): ACHFormValues => {
  const prefillDisabledWithQueryParam = getQueryParam('disablePrefill') === 'true';
  const prefillDisabled = window.s4cConfig.features?.canPrefillBankForm !== true;
  if (prefillDisabled || prefillDisabledWithQueryParam) {
    return initialValues;
  }

  return {
    [FORM_FIELDS.BANK_NAME]: 'Test Bank',
    [FORM_FIELDS.ACCOUNT_OWNER_NAME]: 'John Doe',
    [FORM_FIELDS.ACCOUNT]: {
      [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: '000123456789',
      [NESTED_ACCOUNT_FIELDS.ROUTING_NUMBER]: '110000000',
      [NESTED_ACCOUNT_FIELDS.CHECKING_OR_SAVINGS]: DepositAccountType.CHECKING,
    },
    [FORM_FIELDS.ACCOUNT_OWNER_TYPE]: AccountOwnerType.Business,
    [FORM_FIELDS.BUSINESS_NAME]: 'Test Business',
    [FORM_FIELDS.FIRST_NAME]: undefined,
    [FORM_FIELDS.LAST_NAME]: undefined,
    [FORM_FIELDS.ADDRESS]: {
      [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: '11208 Zena Inlet',
      [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: undefined,
      [NESTED_ACCOUNT_FIELDS.CITY]: 'East Milton',
      [NESTED_ACCOUNT_FIELDS.STATE]: 'NY',
      [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: '61250-4528',
      [NESTED_ACCOUNT_FIELDS.COUNTRY]: 'USA',
    },
  };
};
